import type { QuestionId } from '../model/brands.ts';
import { isAnswered } from '../model/session.ts';
import { elapsedMs, remainingMs } from './clock.ts';
import type { SessionEvent } from './reducer.ts';
import { currentQuestionId, type SessionState } from './state.ts';

/**
 * Derived views of a session.
 *
 * Everything here is computed from the state, never stored on it. Two sources of truth for
 * "how many are answered" is how a progress bar ends up disagreeing with the submit dialog.
 */

export type NavigatorStatus = 'current' | 'answered-marked' | 'marked' | 'answered' | 'unanswered';

export interface NavigatorItem {
  readonly questionId: QuestionId;
  readonly index: number;
  readonly status: NavigatorStatus;
  readonly answered: boolean;
  readonly markedForReview: boolean;
  readonly current: boolean;
}

export function navigatorItems(state: SessionState): readonly NavigatorItem[] {
  return state.blueprint.questionOrder.map((questionId, index) => {
    const entry = state.entries[questionId];
    const answered = isAnswered(entry?.response ?? null);
    const markedForReview = entry?.markedForReview ?? false;
    const current = index === state.cursor;

    return {
      questionId,
      index,
      answered,
      markedForReview,
      current,
      // "Answered and marked" is its own state rather than a colour blend: the two facts matter
      // independently when deciding what to go back to.
      status: current
        ? 'current'
        : answered && markedForReview
          ? 'answered-marked'
          : markedForReview
            ? 'marked'
            : answered
              ? 'answered'
              : 'unanswered',
    };
  });
}

/**
 * Which questions the navigator is showing.
 *
 * VCE calls this "Review All / Marked / Incomplete" and opens a separate screen for it. The
 * navigator is already that screen, permanently on display, so the feature is a filter over it
 * rather than another place to be.
 */
export type NavigatorFilter = 'all' | 'marked' | 'unanswered';

export function filterItems(
  items: readonly NavigatorItem[],
  filter: NavigatorFilter,
): readonly NavigatorItem[] {
  switch (filter) {
    case 'all':
      return items;
    case 'marked':
      return items.filter((item) => item.markedForReview);
    case 'unanswered':
      return items.filter((item) => !item.answered);
  }
}

/**
 * The next question matching a filter, wrapping around; null when there is none.
 *
 * Wrapping matters: the marked questions someone wants to revisit are usually behind them, and a
 * button that does nothing at the end of the list reads as broken rather than as finished.
 */
export function nextMatching(
  state: SessionState,
  filter: NavigatorFilter,
  from: number = state.cursor,
): number | null {
  const items = navigatorItems(state);
  const matching = filterItems(items, filter);
  if (matching.length === 0) {
    return null;
  }

  const ahead = matching.find((item) => item.index > from);
  const target = ahead ?? matching[0];
  if (target === undefined || target.index === from) {
    return null;
  }
  return target.index;
}

/**
 * Whether the questions must stay hidden.
 *
 * The clock is the authority, not the status name. Pausing stops the clock, and the whole point of
 * hiding the questions is that a stopped clock beside a readable exam is unlimited time — so the
 * test is "is time being counted", which also covers the submit dialog opened *from* a pause.
 *
 * A session confirming submission with the clock still running is not stopped: you are still in
 * the exam, still being timed, and still allowed to look at the question you are leaving.
 */
export function isStopped(state: SessionState): boolean {
  if (state.status !== 'paused' && state.status !== 'confirming-submit') {
    return false;
  }
  return state.clock.segments[state.clock.segments.length - 1]?.endedAt !== null;
}

export interface NotedQuestion {
  readonly questionId: QuestionId;
  /** Position in this session, counting from 1 — the number the navigator shows. */
  readonly position: number;
  readonly note: string;
  readonly markedForReview: boolean;
}

/**
 * Every note the candidate wrote, in the order the session asked the questions.
 *
 * Session order rather than file order, because this list is read straight after finishing and the
 * numbers beside it are the ones that were just on screen. The file number is added by the export,
 * where the reader may no longer have the session in front of them.
 */
export function notedQuestions(state: SessionState): readonly NotedQuestion[] {
  const noted: NotedQuestion[] = [];

  state.blueprint.questionOrder.forEach((questionId, index) => {
    const entry = state.entries[questionId];
    if (entry === undefined || entry.note.trim() === '') {
      return;
    }
    noted.push({
      questionId,
      position: index + 1,
      note: entry.note,
      markedForReview: entry.markedForReview,
    });
  });

  return noted;
}

export interface Progress {
  readonly total: number;
  readonly answered: number;
  readonly unanswered: number;
  readonly marked: number;
  readonly position: number;
}

export function progress(state: SessionState): Progress {
  let answered = 0;
  let marked = 0;

  for (const questionId of state.blueprint.questionOrder) {
    const entry = state.entries[questionId];
    if (isAnswered(entry?.response ?? null)) {
      answered += 1;
    }
    if (entry?.markedForReview === true) {
      marked += 1;
    }
  }

  const total = state.blueprint.questionOrder.length;
  return {
    total,
    answered,
    unanswered: total - answered,
    marked,
    position: total === 0 ? 0 : state.cursor + 1,
  };
}

export interface TimeView {
  readonly elapsedMs: number;
  readonly remainingMs: number | null;
  readonly expired: boolean;
  /** True in the last five minutes, or the last tenth of the limit, whichever is shorter. */
  readonly runningOut: boolean;
}

export function timeView(state: SessionState, at: number): TimeView {
  const remaining = remainingMs(state.clock, at);
  const limit = state.clock.timeLimitMs;

  return {
    elapsedMs: elapsedMs(state.clock.segments, at),
    remainingMs: remaining,
    expired: state.clock.expired,
    runningOut:
      remaining !== null && limit !== null && remaining <= Math.min(5 * 60_000, limit / 10),
  };
}

/** The question on screen, with the option order the blueprint drew for it. */
export function currentView(state: SessionState): {
  readonly questionId: QuestionId | undefined;
  readonly optionOrder: readonly string[] | undefined;
} {
  const questionId = currentQuestionId(state);
  return {
    questionId,
    optionOrder: questionId === undefined ? undefined : state.blueprint.optionOrder[questionId],
  };
}

/**
 * Whether an event would do anything.
 *
 * The UI asks this instead of re-deriving the rules, which is what keeps "the button was enabled"
 * and "the action was accepted" from ever disagreeing.
 */
export function can(state: SessionState, event: SessionEvent['type']): boolean {
  const live = state.status === 'active';
  const questionId = currentQuestionId(state);
  const entry = questionId === undefined ? undefined : state.entries[questionId];

  switch (event) {
    case 'PREVIOUS':
      return (
        state.cursor > 0 &&
        (state.policy.allowBackNavigation || state.status === 'reviewing') &&
        state.status !== 'completed'
      );
    case 'NEXT':
      return state.cursor < state.blueprint.questionOrder.length - 1 && state.status !== 'completed';
    case 'NAVIGATE':
      return state.status !== 'completed';
    case 'ANSWER':
      return (
        live &&
        entry?.revealedAt === null &&
        (state.policy.allowChangeAnswer || !isAnswered(entry.response))
      );
    case 'TOGGLE_MARK':
      return state.policy.allowMarkForReview && (live || state.status === 'paused');
    case 'SET_CONFIDENCE':
      return state.policy.collectConfidence && (live || state.status === 'paused');
    case 'SET_NOTE':
      // No policy gates a note, and no status either: they are the candidate's own words.
      return true;
    case 'REVEAL':
      return state.policy.immediateFeedback && entry?.revealedAt === null && live;
    case 'PAUSE':
      return live;
    case 'RESUME':
      return state.status === 'paused';
    case 'SELF_GRADE':
      return state.status === 'completed' || state.status === 'reviewing';
    case 'REPORT':
      return true;
    case 'REQUEST_SUBMIT':
      // Always available: never trap someone inside an exam they want to end.
      return live || state.status === 'paused';
    case 'CANCEL_SUBMIT':
      return state.status === 'confirming-submit';
    case 'CONFIRM_SUBMIT':
      return live || state.status === 'paused' || state.status === 'confirming-submit';
    case 'ENTER_REVIEW':
      return state.status === 'completed';
    case 'EXIT_REVIEW':
      return state.status === 'reviewing';
    case 'VISIBILITY':
    case 'TICK':
      return true;
  }
}
